import Swal from "sweetalert2";

function get_service_error_message(xhr, fallback_message) {
    const response_message = xhr?.responseJSON?.message;
    if (typeof response_message === "string") return response_message;
    return response_message ? JSON.stringify(response_message) : fallback_message;
}

function get_first_service_base_profile(services = []) {
    return services.find(Boolean)?.base_profile || null;
}

function get_category_by_name(categories = [], category_name = "") {
    return categories.find((category) => category.name === category_name);
}

function get_subcategory_by_name(subcategories = [], subcategory_name = "") {
    return subcategories.find((subcategory) => subcategory.name === subcategory_name);
}

function is_success_response(response) {
    return response.status === "success";
}

function get_response_list(value) {
    return Array.isArray(value) ? value : [];
}

export const services_data_methods = {
    get_active_base_services() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_active_base_services/`,
                type: "GET",
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                    "Content-Type": "application/json",
                },
                success: (response) => this.handle_active_services_response(response, resolve, reject),
                error: (xhr) => {
                    reject(new Error(get_service_error_message(xhr, "Failed to load active services.")));
                }
            });
        });
    },
    handle_active_services_response(response, resolve, reject) {
        if (response.status !== "success") {
            reject(new Error(response.message || "Failed to load active services."));
            return;
        }

        const services = response.data || [];
        this.sync_catalogue_items(services);
        this.sync_base_profile_from_services(services);
        resolve(services);
    },
    sync_base_profile_from_services(services) {
        const base_profile = get_first_service_base_profile(services);
        if (this.base_profile_id || !base_profile) return;

        this.shop_profile = base_profile;
        this.base_profile_id = base_profile.id || this.base_profile_id;
    },
    fetch_product_types() {
        return new Promise((resolve, reject) => {
            $.ajax({
                type: "GET",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_product_types/`,
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                },
                success: (response) => this.handle_product_types_response(response, resolve, reject),
                error: (xhr) => {
                    reject(new Error(get_service_error_message(xhr, "Failed to load product types.")));
                }
            });
        });
    },
    handle_product_types_response(response, resolve, reject) {
        if (!is_success_response(response)) {
            reject(new Error(response.message || "Failed to load product types."));
            return;
        }

        this.product_types = get_response_list(response.data);
        this.service_types = get_response_list(response.service_data);
        resolve(this.product_types);
    },
    get_attribute_types() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_attribute_types/`,
                type: "GET",
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                    "Content-Type": "application/json",
                },
                success: (response) => this.handle_attribute_types_response(response, resolve, reject),
                error: (xhr) => {
                    reject(new Error(get_service_error_message(xhr, "Failed to load attribute types.")));
                }
            });
        });
    },
    handle_attribute_types_response(response, resolve, reject) {
        if (!is_success_response(response)) {
            reject(new Error(response.message || "Failed to load attribute types."));
            return;
        }

        this.attributeTypes = get_response_list(response.data);
        resolve(this.attributeTypes);
    },
    get_requests() {
        return new Promise((resolve) => {
            if (!this.base_profile_id) {
                this.requests = [];
                resolve([]);
                return;
            }

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_service_requests/`,
                headers: {
                    Authorization: `Token ${this.getToken()}`,
                },
                data: JSON.stringify({
                    profile_id: this.base_profile_id
                }),
                contentType: "application/json",
                success: (response) => {
                    if (response.status === "success") {
                        this.requests = (response.data || []).map((request) => ({
                            ...request,
                            is_loading: false,
                            show_decline: false,
                        }));
                        resolve(this.requests);
                        return;
                    }

                    this.requests = [];
                    this.errors.requests = response.message || "Failed to load requests.";
                    resolve([]);
                },
                error: (xhr) => {
                    const message = get_service_error_message(xhr, "Failed to load requests.");
                    this.requests = [];
                    this.errors.requests = message;
                    resolve([]);
                }
            });
        });
    },
    fetch_categories() {
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_categories/`,
            type: "GET",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json",
            },
            success: (response) => this.handle_categories_response(response)
        });
    },
    handle_categories_response(response) {
        if (!is_success_response(response)) return;

        this.categories = get_response_list(response.categories);
        this.sync_modal_category_selection();
    },
    sync_modal_category_selection() {
        if (!this.modal_product?.category) return;

        const category = get_category_by_name(this.categories, this.modal_product.category);
        if (!category) return;

        this.selected_category = category.id;
        this.fetch_subcategories(category.id);
    },
    fetch_subcategories(category_id) {
        this.selected_subcategory = "";
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_subcategories/`,
            type: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json",
            },
            data: JSON.stringify({ category_id }),
            success: (response) => this.handle_subcategories_response(response)
        });
    },
    handle_subcategories_response(response) {
        if (!is_success_response(response)) return;

        this.subcategories = get_response_list(response.subcategories);
        this.sync_modal_subcategory_selection();
    },
    sync_modal_subcategory_selection() {
        if (!this.modal_product?.subcategory) return;

        const subcategory = get_subcategory_by_name(this.subcategories, this.modal_product.subcategory);
        if (!subcategory) return;

        this.update_item_form.subcategory_id = subcategory.id;
    },
    update_request(request, type, reason = "") {
        const base_url = `${window.location.origin}/my_services`;
        request.is_loading = true;

        $.ajax({
            type: "POST",
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_service_request_status/`,
            headers: {
                Authorization: `Token ${this.getToken()}`,
            },
            data: JSON.stringify({
                request_id: request.id,
                action: type,
                return_message: reason || "",
                base_url: base_url,
            }),
            contentType: "application/json",
            success: (response) => {
                if (response.status === "success") {
                    Swal.fire({
                        title: "Success",
                        text: "Request updated successfully!",
                        icon: "success",
                        timer: 1200,
                        showConfirmButton: false,
                        position: "bottom-end",
                        toast: true,
                    });
                    this.get_requests();
                }
            },
            error: (xhr) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: xhr.responseJSON?.message || "Failed to update request.",
                    position: "bottom-end",
                    toast: true,
                    showConfirmButton: false,
                    timer: 2500,
                });
            },
            complete: () => {
                request.is_loading = false;
            }
        });
    },
};
